import React from 'react';
import studentService from '../../services/api/student';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getArabicDate } from '../../services/utils/date';
import { getImageUrl } from '../../config/apiConfig';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { 
  BookOpen, 
  CreditCard, 
  TrendingUp, 
  MessageCircle,
  Play,
  Clock,
  Star,
  ChevronLeft,
  GraduationCap,
  Zap,
  CheckCircle2,
  Bell
} from 'lucide-react';

const StudentDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // جلب كورسات الطالب المسجل فيها
const { data: coursesData, isLoading: coursesLoading, error: coursesError } = useQuery(
  ['student-courses', user?.id],
  () => studentService.getEnrolledCourses(),
  {
    retry: 1, 
  }
);

  // جلب النشاط الأخير للطالب
const { data: activityData, isLoading: activityLoading, error: activityError } = useQuery(
  ['student-activity', user?.id],
  () => studentService.getRecentActivity(),
  {
    retry: 1,   
  }
);

  // جلب بيانات الاشتراكات الحقيقية
const { data: subscriptionsData, isLoading: subscriptionsLoading, error: subscriptionsError } = useQuery(
  ['student-subscriptions', user?.id],
  () => studentService.getSubscriptions(),
  {
    retry: 1,
  }
);

  // جلب إحصائيات الطالب
const { data: statsData, isLoading: statsLoading, error: statsError } = useQuery(
  ['student-stats', user?.id],
  () => studentService.getStudentStats(),
  {
    retry: 1,
  }
);

  const isLoading = statsLoading || coursesLoading || activityLoading || subscriptionsLoading;

  // Helper function to get progress value
  const getProgressValue = (course) => {
    return typeof course.progress === 'object' 
      ? course.progress?.completionRate || 0 
      : course.progress || 0;
  };

  // دمج البيانات من مصادر مختلفة
  const mergedStats = React.useMemo(() => {
    const coursesList = coursesData?.courses || [];
    
    // حساب الدروس المكتملة من جميع الكورسات
    const totalLessonsCompleted = coursesList.reduce((total, course) => {
      const progress = typeof course.progress === 'object' ? course.progress?.completedLessons || 0 : 0;
      return total + progress;
    }, 0);
    
    // حساب معدل الإنجاز الكلي
    const totalCompletionRate = coursesList.length > 0 
      ? Math.round(coursesList.reduce((total, course) => {
          const rate = getProgressValue(course);
          return total + rate;
        }, 0) / coursesList.length)
      : 0;

    // حساب الاشتراكات النشطة من بيانات الاشتراكات الحقيقية
    const activeSubscriptions = subscriptionsData?.subscriptions?.filter(sub => sub.status === 'active').length || 0;

    // حساب الأسئلة من النشاط الأخير
    const totalQuestions = activityData?.activities?.filter(activity => activity.type === 'question').length || 0;

    return {
      // بيانات أساسية من API
      active_subscriptions: activeSubscriptions,
      enrolled_courses: coursesList.length,
      completion_rate: totalCompletionRate,
      total_questions: totalQuestions,
      
      // قائمة الكورسات
      enrolled_courses_list: coursesList,
      
      // النشاط الأخير
      recent_activity: activityData?.activities || [],
      
      // بيانات إضافية محسوبة
      total_lessons_completed: totalLessonsCompleted,
      total_watch_time: statsData?.total_watch_time || 0,
      achievements: statsData?.achievements || [],
      current_streak: statsData?.current_streak || 0
    };
  }, [statsData, coursesData, activityData, subscriptionsData]);

  // تكوين بطاقات الإحصائيات (مع البيانات الحقيقية من API)
  const statCards = [
    {
      title: 'اشتراكاتي',
      value: mergedStats.active_subscriptions,
      label: 'اشتراك نشط',
      icon: CreditCard,
      color: 'from-blue-600 to-blue-700',
      shadow: 'shadow-blue-100',
      change: mergedStats.active_subscriptions > 0 ? 'نشط' : 'غير نشط'
    },
    {
      title: 'الكورسات',
      value: mergedStats.enrolled_courses,
      label: 'كورس مشترك',
      icon: BookOpen,
      color: 'from-green-600 to-green-700',
      shadow: 'shadow-green-100',
      change: `+${mergedStats.total_lessons_completed} درس`
    },
    {
      title: 'إجمالي التقدم',
      value: `${mergedStats.completion_rate}%`,
      label: 'معدل الإنجاز',
      icon: TrendingUp,
      color: 'from-purple-600 to-purple-700',
      shadow: 'shadow-purple-100',
      change: mergedStats.current_streak > 0 ? `${mergedStats.current_streak} أيام متتالية` : 'ابدأ الآن'
    },
    {
      title: 'الأسئلة',
      value: mergedStats.total_questions,
      label: 'استفسار مقدم',
      icon: MessageCircle,
      color: 'from-orange-600 to-orange-700',
      shadow: 'shadow-orange-100',
      change: mergedStats.total_questions > 0 ? 'نشط' : 'اطرح سؤال'
    }
  ];

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse">جاري تحضير لوحة التحكم الخاصة بك...</p>
      </div>
    );
  }

  // Error handling for any query failures
  if (statsError || coursesError || activityError || subscriptionsError) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="text-center py-24">
          <div className="bg-red-50 p-10 rounded-[2.5rem] border border-red-100 max-w-md mx-auto">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-xl font-black text-gray-900 mb-2">حصل خطأ أثناء تحميل لوحة التحكم</h3>
            <p className="text-gray-600 text-sm mb-6">يرجى التأكد من اتصالك بالإنترنت والمحاولة مرة أخرى</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-red-600 text-white px-6 py-3 rounded-2xl font-bold hover:bg-red-700 transition-all"
            >
              إعادة المحاولة
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-8">
      
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight flex items-center gap-3">
            لوحة التحكم الرئيسية
            <span className="text-sm font-bold bg-primary-50 text-primary-600 px-3 py-1 rounded-full border border-primary-100">
                حساب طالب
            </span>
          </h1>
          <p className="text-gray-500 mt-1 font-medium flex items-center gap-2">
            <GraduationCap className="text-primary-600" size={18} />
            أهلاً بك {user?.name || 'يا طالب'}، إليك نظرة سريعة على تقدمك التعليمي اليوم.
          </p>
        </div>
        <div className="flex items-center gap-3">
            <button className="p-3 bg-white border border-gray-100 rounded-2xl text-gray-400 hover:text-primary-600 transition-all shadow-sm">
                <Bell size={20} />
            </button>
            <div className="h-10 w-[1px] bg-gray-100 mx-2"></div>
            <p className="text-sm font-bold text-gray-700">{getArabicDate(new Date())}</p>
        </div>
      </div>

      {/* Modern Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.title} className={`relative overflow-hidden bg-gradient-to-br ${card.color} rounded-[2rem] p-6 text-white shadow-xl ${card.shadow} transition-transform hover:-translate-y-1 duration-300`}>
              <div className="absolute top-0 left-0 w-24 h-24 bg-white/10 rounded-br-full -z-0"></div>
              <div className="relative z-10">
                <div className="bg-white/20 w-12 h-12 rounded-xl flex items-center justify-center mb-6 backdrop-blur-md">
                  <Icon size={24} />
                </div>
                <p className="text-white/70 text-xs font-black uppercase tracking-widest mb-1">{card.title}</p>
                <div className="flex items-end gap-2 mb-4">
                    <p className="text-4xl font-black">{card.value}</p>
                    <span className="text-xs font-bold opacity-80 mb-1.5">{card.label}</span>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] font-bold bg-black/10 w-fit px-3 py-1 rounded-full">
                  <TrendingUp size={12} />
                  <span>{card.change} من الشهر الماضي</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Main Section: My Courses (2/3 Width) */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-[2.5rem] shadow-sm p-8 border border-gray-100">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-black text-gray-900 flex items-center gap-3">
                <div className="bg-primary-600 w-2 h-8 rounded-full"></div>
                كورساتي الحالية
              </h2>
              <button 
                onClick={() => navigate('/student/courses')}
                className="text-primary-600 font-bold text-sm flex items-center gap-1 hover:gap-2 transition-all"
              >
                عرض الكل <ChevronLeft size={18} />
              </button>
            </div>

            {mergedStats.enrolled_courses_list?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {mergedStats.enrolled_courses_list.slice(0, 4).map((course) => (
                    <div key={course.id} className="group bg-gray-50/50 border border-gray-100 rounded-3xl overflow-hidden hover:bg-white hover:shadow-xl hover:border-primary-100 transition-all duration-300">
                    <div className="relative h-40">
                        {/* ✅ نفس منطق StudentCourseCard: استخدام getImageUrl مع fallback مباشر */}
                        <img
                          src={course.thumbnail ? getImageUrl(course.thumbnail) : '/default_course.png'}
                          alt={course.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          onError={(e) => {
                            e.target.src = '/default_course.png';
                          }}
                          onLoad={(e) => {
                            // Image loaded successfully
                          }}
                        />
                        <div className="absolute top-3 left-3 bg-black/50 backdrop-blur-md text-white text-[10px] font-bold px-3 py-1 rounded-lg flex items-center gap-1">
                          <Star size={12} className="text-yellow-400 fill-yellow-400" />
                          {course.rating || '5.0'}
                        </div>
                    </div>
                                        
                    <div className="p-5">
                        <h3 className="font-black text-gray-900 mb-2 line-clamp-1 group-hover:text-primary-600 transition-colors">{course.title}</h3>
                        <div className="flex items-center gap-2 mb-4 text-xs font-bold text-gray-500">
                            <div className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-[10px]">
                                {course.teacher_name?.[0] || '؟'}
                            </div>
                            أ / {course.teacher_name}
                        </div>

                        <div className="space-y-3 mb-5">
                            <div className="flex items-center justify-between text-[10px] font-black uppercase">
                                <span className="text-gray-400">التقدم الدراسي</span>
                                <span className="text-primary-600">
                                    {getProgressValue(course)}%
                                </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                                <div 
                                    className="bg-primary-600 h-full rounded-full transition-all duration-1000"
                                    style={{ width: `${getProgressValue(course)}%` }}
                                />
                            </div>
                        </div>

                        <button
                            onClick={() => navigate(`/courses/${course.id}`)}
                            className="w-full py-3 bg-white border border-gray-200 text-gray-700 font-bold rounded-2xl hover:bg-primary-600 hover:text-white hover:border-primary-600 transition-all flex items-center justify-center gap-2 text-sm shadow-sm transform active:scale-95"
                        >
                            <Play size={16} fill="currentColor" />
                            متابعة الدرس
                        </button>
                    </div>
                    </div>
                ))}
                </div>
            ) : (
                <div className="py-12 text-center bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                    <div className="w-24 h-24 mx-auto mb-4">
                        <div className="w-full h-full bg-gradient-to-br from-primary-50 to-primary-100 rounded-[2.5rem] flex items-center justify-center shadow-sm">
                            <div className="text-center">
                                <div className="w-12 h-12 mx-auto mb-2 bg-primary-200 rounded-2xl flex items-center justify-center">
                                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                    </svg>
                                </div>
                                <p className="text-xs text-primary-600 font-medium">كورس تعليمي</p>
                            </div>
                        </div>
                    </div>
                    <p className="text-gray-500 font-bold">أنت غير مشترك في أي كورسات حالياً.</p>
                    <button onClick={() => navigate('/student/browse')} className="mt-4 text-primary-600 font-black text-sm">استكشف الكورسات الآن</button>
                </div>
            )}
          </div>

          {/* Quick Action Grid */}
          <div className="bg-white rounded-[2.5rem] shadow-sm p-8 border border-gray-100">
            <h2 className="text-2xl font-black text-gray-900 mb-8 flex items-center gap-3">
                <div className="bg-primary-600 w-2 h-8 rounded-full"></div>
                إجراءات سريعة
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <button onClick={() => navigate('/student/browse')} className="p-6 bg-blue-50/50 border border-blue-100 rounded-3xl hover:bg-blue-600 hover:text-white transition-all group text-right">
                    <div className="bg-white w-12 h-12 rounded-2xl flex items-center justify-center mb-4 text-blue-600 group-hover:bg-blue-500 group-hover:text-white transition-colors shadow-sm">
                        <BookOpen size={24} />
                    </div>
                    <p className="font-black text-gray-900 group-hover:text-white mb-1">تصفح المكتبة</p>
                    <p className="text-[10px] text-gray-500 group-hover:text-white/80 font-bold uppercase">اكتشف محتوى جديد</p>
                </button>

                <button onClick={() => navigate('/student/questions')} className="p-6 bg-orange-50/50 border border-orange-100 rounded-3xl hover:bg-orange-600 hover:text-white transition-all group text-right">
                    <div className="bg-white w-12 h-12 rounded-2xl flex items-center justify-center mb-4 text-orange-600 group-hover:bg-orange-500 group-hover:text-white transition-colors shadow-sm">
                        <MessageCircle size={24} />
                    </div>
                    <p className="font-black text-gray-900 group-hover:text-white mb-1">اسأل المدرس</p>
                    <p className="text-[10px] text-gray-500 group-hover:text-white/80 font-bold uppercase">احصل على إجابات</p>
                </button>

                <button onClick={() => navigate('/student/subscriptions')} className="p-6 bg-purple-50/50 border border-purple-100 rounded-3xl hover:bg-purple-600 hover:text-white transition-all group text-right">
                    <div className="bg-white w-12 h-12 rounded-2xl flex items-center justify-center mb-4 text-purple-600 group-hover:bg-purple-500 group-hover:text-white transition-colors shadow-sm">
                        <CreditCard size={24} />
                    </div>
                    <p className="font-black text-gray-900 group-hover:text-white mb-1">اشتراكاتي</p>
                    <p className="text-[10px] text-gray-500 group-hover:text-white/80 font-bold uppercase">إدارة الاشتراكات</p>
                </button>
            </div>
          </div>
        </div>

        {/* Sidebar Section: Recent Activity (1/3 Width) */}
        <div className="space-y-8">
          <div className="bg-white rounded-[2.5rem] shadow-sm p-8 border border-gray-100">
            <h2 className="text-xl font-black text-gray-900 mb-6 flex items-center gap-3">
              <Zap className="text-primary-600" size={20} />
              النشاط الأخير
            </h2>
            <div className="space-y-6">
              {mergedStats.recent_activity?.length > 0 ? (
                mergedStats.recent_activity.map((activity) => (
                    <div key={activity.id || activity.description} className="relative flex items-start gap-4 pb-6 last:pb-0 group">
                      {/* Timeline Line */}
                      {mergedStats.recent_activity.indexOf(activity) !== mergedStats.recent_activity.length - 1 && (
                          <div className="absolute top-10 right-5 w-[2px] h-full bg-gray-100 group-hover:bg-primary-100 transition-colors"></div>
                      )}
                      
                      <div className={`flex-shrink-0 w-10 h-10 rounded-2xl flex items-center justify-center shadow-sm z-10 ${
                        activity.type === 'lesson' ? 'bg-blue-50 text-blue-600' : 
                        activity.type === 'question' ? 'bg-green-50 text-green-600' : 'bg-purple-50 text-purple-600'
                      }`}>
                        {activity.type === 'lesson' && <Play size={18} fill="currentColor" />}
                        {activity.type === 'question' && <MessageCircle size={18} />}
                        {activity.type === 'subscription' && <CreditCard size={18} />}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-800 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                            {activity.description}
                        </p>
                        <div className="flex items-center text-[10px] text-gray-400 font-bold mt-1.5 gap-1.5 uppercase tracking-wider">
                          <Clock size={12} />
                          {activity.time}
                        </div>
                      </div>
                    </div>
                ))
              ) : (
                <div className="text-center py-6">
                    <p className="text-xs text-gray-400 font-bold">لا يوجد نشاط مسجل مؤخراً.</p>
                </div>
              )}
            </div>
          </div>

          {/* Achievement Card */}
          <div className="bg-gray-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden group shadow-2xl shadow-primary-100">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary-600/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
            <div className="relative z-10">
                <div className="bg-primary-600 w-12 h-12 rounded-2xl flex items-center justify-center mb-6">
                    <CheckCircle2 size={24} />
                </div>
                <h3 className="text-xl font-black mb-2">استمر في التقدم!</h3>
                <p className="text-gray-400 text-sm font-medium leading-relaxed mb-6">
                    بإمكانك إنهاء مادة كاملة هذا الأسبوع إذا حافظت على معدل مشاهدة 3 فيديوهات يومياً.
                </p>
                <div className="bg-white/10 p-4 rounded-2xl border border-white/10 flex items-center justify-between">
                    <span className="text-xs font-bold">المستوى الحالي:</span>
                    <span className="text-primary-400 font-black tracking-widest">طالب متميز</span>
                </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default StudentDashboard;